import { type KeyboardEvent, type ReactElement } from "react";
import { color, radius, space } from "@tokens/index";
import { Text } from "../../primitives";
import { rowMain, selectionBar } from "../queue-surfaces/listSurface";
import styles from "./RailRow.module.css";

/**
 * One dense rail row, 36–40px. Line one: icon, label, trailing badge. Line
 * two: the status caption, then the sublabel, when present. The title owns
 * the full width so it survives the 240/260 rail.
 *
 * Content owns the height: the row never shrinks to share the viewport with
 * its siblings and clips its own overflow, so one card's text cannot paint
 * through the next. Hover and selected fill stay in the stylesheet so pointer
 * motion does not re-render the row.
 */
export function RailRow({
  label,
  sublabel,
  icon,
  selected,
  onSelect,
  accent,
  trailing,
  edgeBarColor,
  caption,
}: {
  readonly label: string;
  readonly sublabel: string | undefined;
  readonly icon: ReactElement;
  readonly selected: boolean;
  readonly onSelect: () => void;
  readonly accent: string;
  readonly trailing: ReactElement | undefined;
  /** Status color painted as a 3px bar on the leading edge. Task rows only. */
  readonly edgeBarColor?: string;
  /** Inline status caption, rendered first on line two. */
  readonly caption?: ReactElement;
}): ReactElement {
  const barColor = edgeBarColor ?? (selected ? accent : undefined);
  const hasSublabel = sublabel !== undefined && sublabel.length > 0;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      data-list-row=""
      data-selected={selected || undefined}
      className={styles.row}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      style={{
        width: "100%",
        boxSizing: "border-box",
        height: "auto",
        minHeight: 36,
        flexShrink: 0,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        gap: space[2],
        textAlign: "left",
        padding: `${space[1]} ${space[3]}`,
        paddingLeft: space[4],
        borderRadius: radius.md,
        cursor: "pointer",
        position: "relative",
        color: color.fg.default,
      }}
    >
      {barColor !== undefined && (
        <span aria-hidden="true" style={{ ...selectionBar, background: barColor }} />
      )}
      <span style={{ display: "inline-flex", flexShrink: 0, color: accent }}>{icon}</span>
      <span style={{ ...rowMain, gap: 1 }}>
        <Text role="body" size="sm" weight="medium" truncate>
          {label}
        </Text>
        {(hasSublabel || caption !== undefined) && (
          <span className={styles.meta}>
            {caption}
            {hasSublabel && (
              <Text
                role="caption"
                size="xs"
                tone="subtle"
                truncate
                {...(caption !== undefined ? { className: styles.preview } : {})}
                style={{ flex: 1, minWidth: 0 }}
              >
                {sublabel}
              </Text>
            )}
          </span>
        )}
      </span>
      {trailing !== undefined && (
        <span style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", minHeight: 20 }}>
          {trailing}
        </span>
      )}
    </div>
  );
}
